import React, { useCallback, useMemo } from 'react';

import { Platform, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import {
    SafeAreaView,
    useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { CloseIcon } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    PROFILE_CLOSE_BUTTON,
    SELECT_PROFILE_TYPE_DESCRIPTION,
    SELECT_PROFILE_TYPE_TITLE,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { changeUserProfileRating } from 'store/user/actions';

import useBackHandler from 'utils/hooks/useBackHandler.ts';
import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import SvgButton from 'components/CustomButtons/SvgButton';
import DialogBody from 'components/Dialog/components/DialogBody';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import ProfileTypeItem from '../components/ProfileTypeItem';
import profileStyles, { PHONE_TOP_PADDING } from '../styles';
import { nullFunction } from '../utils';
import styles from './styles';

const {
    PROFILE: {
        PROFILES_ADD_NEW_PROFILE_TITLE,
        PROFILES_ADD_NEW_PROFILE_TYPE_DESCRIPTION,
    },
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_CLOSE_BUTTON },
    },
} = LANGUAGE_KEYS;

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.ADD_PROFILE>;
};

const ProfileType = ({ navigation }: Props) => {
    const { t } = useTranslation();
    const { top } = useSafeAreaInsets();

    const isRootNavigator = useMemo(
        () => !navigation.canGoBack(),
        [navigation],
    );

    const dispatch = useDispatch();

    const handleOnPressProfileType = (isChildProfile: boolean) => {
        dispatch(changeUserProfileRating(null));

        navigation.navigate(SCREEN.ADD_PROFILE, {
            isChildModeEnabled: isChildProfile,
        });
    };

    const handleClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const handleHardwareBackPress = useCallback(() => false, []);

    useBackHandler(handleHardwareBackPress);

    const renderCloseComponent = useCallback(
        () =>
            !isRootNavigator && !isWeb ? (
                <SvgButton
                    Svg={CloseIcon}
                    style={[profileStyles.closeButton, { top }]}
                    svgStyle={profileStyles.closeSvg}
                    onPress={handleClose}
                    testID={PROFILE_CLOSE_BUTTON}
                    accessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
                    accessibilityRole={Platform.select({
                        web: 'imagebutton',
                        default: 'button',
                    })}
                    accessible
                />
            ) : null,
        [handleClose, isRootNavigator, top],
    );

    const renderDescriptionComponent = useCallback(
        () => (
            <View>
                <View style={profileStyles.descriptionContainer}>
                    <Text
                        category={ETextCategory.body1}
                        color={ETextColor.light_grey}
                        style={profileStyles.description}
                        testID={SELECT_PROFILE_TYPE_DESCRIPTION}
                        center
                        {...(isWeb && { tabIndex: 0 })}
                    >
                        {t(PROFILES_ADD_NEW_PROFILE_TYPE_DESCRIPTION)}
                    </Text>
                </View>
                <View
                    style={[
                        profileStyles.profilesContentContainer,
                        styles.profilesTypeContentContainer,
                    ]}
                >
                    <View style={styles.profilesTypeContainer}>
                        <ProfileTypeItem
                            onPress={handleOnPressProfileType}
                            style={styles.profileItem}
                        />
                        <ProfileTypeItem
                            onPress={handleOnPressProfileType}
                            isChildProfile
                        />
                    </View>
                </View>
            </View>
        ),
        [handleOnPressProfileType],
    );

    return (
        <SafeAreaView style={styles.container}>
            <DialogBody
                title={PROFILES_ADD_NEW_PROFILE_TITLE}
                titleTestID={SELECT_PROFILE_TYPE_TITLE}
                style={[
                    profileStyles.dialog,
                    {
                        paddingTop:
                            top + (isTabletOrWeb ? 0 : PHONE_TOP_PADDING),
                    },
                ]}
                DescriptionComponent={renderDescriptionComponent}
                handleActionButtonPress={nullFunction}
            />
            {renderCloseComponent()}
        </SafeAreaView>
    );
};

export default ProfileType;
